import { DateTime } from 'luxon'

import { digObject } from '@domain-group/javascript-utils'

import useSelector from '@hooks/useSelector'

import type { EntitiesState } from '@redux/entities'
import type { ProviderModel } from '@models/types'
import type { ProviderDeadlineGroupDeadlines as Deadlines } from '@domain-group/realhub-backend-api-types'

const timeNow = DateTime.now()
const tomorrow = timeNow.plus({ day: 1 })

type GetAgencyDeadlinesParams = {
  agencyProviderDeadlineGroups: EntitiesState['agencyProviderDeadlineGroups'],
  provider: ProviderModel,
  providerDeadlineGroups: EntitiesState['providerDeadlineGroups'],
}

const getAgencyDeadlines = (params: GetAgencyDeadlinesParams) => {
  const {
    agencyProviderDeadlineGroups,
    provider,
    providerDeadlineGroups,
  } = params

  // Use provider deadlines as base
  let { deadlines = {} } = provider

  // Check if the agency is assigned to a deadline group
  // If they are, merge those deadlines with the provider deadlines
  Object.values(agencyProviderDeadlineGroups).some((agencyProviderDeadlineGroup) => {
    const { provider_id, provider_deadline_group_id } = agencyProviderDeadlineGroup

    if (provider_id === provider.id){
      const providerDeadlineGroup = providerDeadlineGroups[provider_deadline_group_id]
      if (providerDeadlineGroup && providerDeadlineGroup.deadlines){
        deadlines = {
          ...deadlines,
          ...providerDeadlineGroup.deadlines,
        }
      }

      return true
    }

    return false
  })

  return deadlines
}

type CheckPassedServiceDeadlineTimeParams = {
  agencyDeadlines: Deadlines,
  serviceId: number,
}

const checkPassedServiceDeadlineTime = (params: CheckPassedServiceDeadlineTimeParams) => {
  const { agencyDeadlines, serviceId } = params

  if (!serviceId || !agencyDeadlines){
    return false
  }

  const matchedServiceDeadlineKey = Object.keys(agencyDeadlines).find((agencyDeadlineKey) => {
    // e.g. key = service_5
    const deadlineServiceId = Number(agencyDeadlineKey.split('_')[1])
    return deadlineServiceId === serviceId
  })

  if (!matchedServiceDeadlineKey){
    return false
  }

  const agencyDeadline = agencyDeadlines[matchedServiceDeadlineKey]

  const deadlineTime = DateTime.fromFormat(agencyDeadline.time, 'hh:mm')

  return timeNow >= deadlineTime
}

type BuildDisabledDatesParams = {
  blockoutDates: string[],
  convertToJsDate?: boolean,
}

const buildDisabledDates = (params: BuildDisabledDatesParams) => {
  const { blockoutDates, convertToJsDate } = params

  const mappedDates = blockoutDates.map((date) => {
    const dateTimeObj = DateTime.fromFormat(date, 'dd/MM/yyyy')
    return convertToJsDate ? dateTimeObj.toJSDate() : dateTimeObj
  })

  return mappedDates
}

type ConvertStringToDateParams = {
  convertToJsDate?: boolean,
  dateString: string,
}

const convertStringToDate = (params: ConvertStringToDateParams) => {
  const { convertToJsDate, dateString } = params

  if (!dateString) return undefined

  const dateObj = DateTime.fromFormat(dateString, 'dd/MM/yyyy')

  return convertToJsDate ? dateObj.toJSDate() : dateObj
}

type UseDateSelectWithDeadlinesOptions = {
  convertToJsDate?: boolean,
  provider: ProviderModel,
  serviceId: number,
}

function useDateSelectWithDeadlines(options: UseDateSelectWithDeadlinesOptions) {
  const { convertToJsDate, provider, serviceId } = options

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencyProviderDeadlineGroups, providerDeadlineGroups } = entities

  const agencyDeadlines = getAgencyDeadlines({ agencyProviderDeadlineGroups, provider, providerDeadlineGroups })

  const hasPassedDeadlineTime = checkPassedServiceDeadlineTime({ agencyDeadlines, serviceId })

  // array of date strings e.g. ['13/09/2023', '17/09/2023']
  const blockoutDates = digObject(provider, 'blockout_dates.dates', [])

  // convert strings to date objects
  const excludeDates = buildDisabledDates({ blockoutDates, convertToJsDate })

  // e.g. '11/01/2024'
  const maxDateString = digObject(provider, 'blockout_dates.max_date', undefined)

  return {
    blockoutDates,
    excludeDates,
    hasPassedDeadlineTime,
    maxDateObj: convertStringToDate({ dateString: maxDateString, convertToJsDate }),
    tomorrow: convertToJsDate ? tomorrow.toJSDate() : tomorrow,
  }
}

export default useDateSelectWithDeadlines
